import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Lock } from "lucide-react";

// Single-field admin login. Only the password matters — the backend matches
// it against any user with role='admin', so the seeded admin email is
// irrelevant.
export default function LoginPage() {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { adminLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Enter the admin password.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await adminLogin(password);
      toast.success("Welcome back!");
      if (data?.role === "admin") {
        navigate("/admin/calendar", { replace: true });
      } else {
        navigate(location.state?.from || "/", { replace: true });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Wrong password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center p-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-stone-600 hover:text-stone-900 mb-8 group" data-testid="login-back-home">
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to home
        </Link>

        <div className="bg-white rounded-3xl border border-stone-200 shadow-xl p-8 md:p-10">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-12 h-12 rounded-2xl bg-brand-100 flex items-center justify-center">
              <Lock className="w-5 h-5 text-brand-700" />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-700">Owner access</div>
              <h2 className="text-2xl font-extrabold text-stone-900 leading-tight" style={{ fontFamily: "Outfit" }}>
                Admin sign in
              </h2>
            </div>
          </div>
          <p className="text-sm text-stone-500 mt-3">
            Enter the admin password to access the dashboard, quote requests, and calendar.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-stone-600 mb-2 block">
                Admin password
              </span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full px-5 py-4 bg-stone-50 border border-stone-200 rounded-full focus:outline-none focus:ring-2 focus:ring-brand-500 focus:bg-white focus:border-transparent transition-all text-lg tracking-wider"
                placeholder="••••••••"
                autoFocus
                required
              />
            </label>
            <button
              type="submit"
              disabled={submitting || !password}
              data-testid="login-submit-btn"
              className="w-full px-6 py-4 bg-brand-600 hover:bg-brand-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold rounded-full transition-all hover:-translate-y-0.5 shadow-lg flex items-center justify-center gap-2"
            >
              {submitting ? "Signing in..." : "Unlock admin"}
              {!submitting && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>
        </div>

        <div className="text-center text-xs text-stone-500 mt-6">
          © {new Date().getFullYear()} Wynridge Exterior Cleaning
        </div>
      </div>
    </div>
  );
}
